import { useId, useMemo } from "react";
import type { View } from "@common/types/views/views";
import { ViewDensity } from "@common/types/views/views";
import type { ViewConfig } from "@lib/views";
import { ViewTable } from "@design-system/ViewTable/ViewTable";
import previewStyles from "../DatePickerPlayground.module.scss";
import tableStyles from "../TablesPlayground.module.scss";
import { PlaygroundSelect } from "../playgroundControls";
import type { ReorderRow } from "./viewTablePlaygroundShared";

export type DataState = "loading" | "empty";

export interface LoadingAndEmptyState {
    view: View;
    dataState: DataState;
}

export interface LoadingAndEmptyScenarioProps {
    density: ViewDensity;
    state: LoadingAndEmptyState;
    onDataStateChange: (dataState: DataState) => void;
}

export const createLoadingAndEmptyState = (): LoadingAndEmptyState => ({
    view: {
        columns: [{ id: "name" }, { id: "owner" }],
        filters: {},
        options: {
            density: ViewDensity.MD,
        },
    },
    dataState: "loading",
});

const config: ViewConfig<ReorderRow, "name" | "owner", never> = {
    columns: {
        name: {
            accessorKey: "name",
            name: "Name",
            header: "Name",
            minSize: 240,
        },
        owner: {
            accessorKey: "owner",
            name: "Owner",
            header: "Owner",
            minSize: 180,
        },
    },
};

const DATA_STATE_LABELS: Record<DataState, string> = {
    loading: "Loading",
    empty: "Empty",
};

const dataStateOptions = [
    { value: "loading", label: DATA_STATE_LABELS.loading },
    { value: "empty", label: DATA_STATE_LABELS.empty },
] satisfies ReadonlyArray<{ value: DataState; label: string }>;

export const LoadingAndEmptyScenario = ({
    density,
    state,
    onDataStateChange,
}: LoadingAndEmptyScenarioProps) => {
    const labelId = useId();
    const viewWithDensity = useMemo<View>(
        () => ({
            ...state.view,
            options: { ...state.view.options, density },
        }),
        [state.view, density],
    );

    return (
        <>
            <div className={tableStyles.controlLabel}>
                <span id={labelId}>Data state</span>
                <PlaygroundSelect<DataState>
                    options={dataStateOptions}
                    value={state.dataState}
                    onChange={onDataStateChange}
                    aria-labelledby={labelId}
                />
            </div>
            <div className={tableStyles.tableFrame}>
                <ViewTable
                    data={state.dataState === "loading" ? null : []}
                    view={viewWithDensity}
                    config={config}
                    enableColumnResizing={false}
                    outerBorder
                />
            </div>
            <div className={previewStyles.output}>
                <span>State</span>
                <div role="status" aria-live="polite">
                    Data state: {DATA_STATE_LABELS[state.dataState]}
                </div>
            </div>
        </>
    );
};
